import React, { useEffect, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { PBB_NAME } from 'src/payments/constants';
import type { MenuImageBannerSlide } from 'src/components/menu-image-banner';
import type { ValidIncentiveValue } from 'src/payments/hooks/shared/incentive-types';
import { useIncentives, UserType } from 'src/payments/hooks/use-incentives/use-incentives';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { IncentiveHomeBanner } from 'src/payments/components/incentive-home-banner';

export const TEST_ID_CASH_BACK_HOME_BANNER = 'cash-back-home-banner';

const EXPOSURE_VISIBILITY_THRESHOLD = 0.5;

type CashBackHomeBannerContentProps = {
  cashBackValue: ValidIncentiveValue;
  userType: UserType;
  isCashBack: boolean;
  onLinkBank: () => void;
};

const CashBackHomeBannerContent = ({
  cashBackValue,
  userType,
  isCashBack,
  onLinkBank,
}: CashBackHomeBannerContentProps): JSX.Element => {
  const bannerRef = useRef<HTMLElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const visibilityEntry = useIntersectionObserver(bannerRef, {
    threshold: EXPOSURE_VISIBILITY_THRESHOLD,
    freezeOnceVisible: hasBeenVisible,
  });
  const isActionable = userType === UserType.UNENROLLED;

  useEffect(() => {
    if (
      !hasBeenVisible &&
      visibilityEntry?.isIntersecting &&
      visibilityEntry.intersectionRatio >= EXPOSURE_VISIBILITY_THRESHOLD
    ) {
      setHasBeenVisible(true);
    }
  }, [hasBeenVisible, visibilityEntry?.intersectionRatio, visibilityEntry?.isIntersecting]);

  useIncentiveExposure({
    incentiveValue: hasBeenVisible ? cashBackValue : undefined,
    userType,
    isCashBack,
    source: 'home_banner',
  });

  return (
    <IncentiveHomeBanner
      headline={
        <>
          Get ${cashBackValue} back on your first purchase with {PBB_NAME}!
        </>
      }
      body={<>Link your bank, and get ${cashBackValue} cash back the first time you pay for an order with your bank.</>}
      disclaimer={<>*Only valid the first time you use {PBB_NAME} to pay for your order.</>}
      ctaLabel='Link your bank now'
      isActionable={isActionable}
      onCta={onLinkBank}
      ctaAriaLabel={`Link your bank to claim ${PBB_NAME} cash back`}
      ariaLabel={`${PBB_NAME} cash back promotion`}
      testId={TEST_ID_CASH_BACK_HOME_BANNER}
      customizationPrefix='cash-back'
      bannerRef={bannerRef}
    />
  );
};

export const useCashBackHomeBannerSlide = (): MenuImageBannerSlide | null => {
  const UI = useUI();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const { showCashBack, cashBackValue } = useCashBackSurfaceGate('homeBanner');
  const { userType, isCashBack } = useIncentives();
  const { openDutchiePay } = useOpenDutchiePay({ enrollmentSource: `home-page-${String(UI?.variant)}` });

  const surfaceSuppressed = UI.isKiosk || UI.isMobileEcommApp;
  const showBanner = showCashBack && cashBackValue !== undefined && isLoggedIn && !surfaceSuppressed;

  if (!showBanner || cashBackValue === undefined) {
    return null;
  }

  return {
    id: TEST_ID_CASH_BACK_HOME_BANNER,
    content: (
      <CashBackHomeBannerContent
        cashBackValue={cashBackValue}
        userType={userType}
        isCashBack={isCashBack}
        onLinkBank={openDutchiePay}
      />
    ),
  };
};

export const CashBackHomeBanner = (): JSX.Element | null => {
  const slide = useCashBackHomeBannerSlide();

  return slide ? <>{slide.content}</> : null;
};
